import { uniqueId } from 'lodash';
import React, { useEffect, useState } from 'react';
import { Card, Container } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { makeApiRequests } from '../../helpers/api';
import { ENDPOINTS, additionalDocHubfieldTypesWithDropdowns } from '../../helpers/constants';
import Loader from '../Loader';
import FormBuilder from '../common/form-builder/FormBuilder';

const validateFields = fields => {
  const keys = new Set();

  for (let field of fields) {
    field.label = field.label.trim();
    field.key = (field.key || '').trim();

    if (!field.label || !field.key) {
      return 'Please provide a label (and key) for all fields';
    }

    if (!Array.isArray(field.models) || field.models.length === 0) {
      return `Field '${field.label}' must belong to at least one model (Contact, Contract, or Inventory)`;
    }

    if (keys.has(field.key)) {
      return `Key '${field.key}' is not unique`;
    }

    keys.add(field.key);

    if (additionalDocHubfieldTypesWithDropdowns.includes(field.type)) {
      if (!field.takeOptionsFrom) {
        return `Field '${field.label}' requires an option type`;
      }

      if (
        field.takeOptionsFrom === 'manual' &&
        (!field.dropdownOptions || field.dropdownOptions.length === 0)
      ) {
        return `Field '${field.label}' requires at least one option`;
      }

      if (field.takeOptionsFrom === 'manual') {
        const options = new Set();

        for (let option of field.dropdownOptions) {
          const optionValue = (typeof option === 'string' ? option : option?.value || '').trim();
          if (!optionValue) {
            return `Option in field '${field.label}' cannot be empty`;
          }

          if (options.has(optionValue)) {
            return `Option '${optionValue}' in field '${field.label}' is not unique`;
          }

          options.add(optionValue);
        }
      }
    }
  }

  return null;
};

const byId = _id => item => item._id === _id;

const findChanges = (originalFields, currentFields) => {
  const newItems = currentFields.filter(item => !item._id);
  const updatedItems = currentFields.filter(item => item._id);
  const deletedItems = originalFields.filter(item => !currentFields.some(byId(item._id))).map(item => item._id);

  return {
    newItems,
    updatedItems,
    deletedItems
  };
};

const normalizeFieldModels = field => {
  if (Array.isArray(field.models) && field.models.length) {
    return field;
  }
  if (field.model) {
    const { model, ...rest } = field;
    return { ...rest, models: [model] };
  }
  return { ...field, models: field.models || [] };
};

const getDropdownOptionEntries = field => {
  if (Array.isArray(field.dropdownOptions) && field.dropdownOptions.length) {
    return field.dropdownOptions.map(o => ({
      value: typeof o === 'string' ? o : String(o?.value ?? o?.label ?? ''),
      uuid: uniqueId()
    }));
  }

  return (field.options || []).map(o => ({
    value: typeof o === 'string' ? o : String(o?.value ?? o?.label ?? ''),
    uuid: uniqueId()
  }));
};

const setUUIDs = response =>
  response.map(r => {
    const normalized = normalizeFieldModels(r);
    return {
      ...normalized,
      uuid: uniqueId(),
      dropdownOptions: getDropdownOptionEntries(normalized),
      // Keep legacy options present for any leftover reads; UI edits dropdownOptions
      options: (normalized.options || []).map(o => ({ ...o, uuid: uniqueId() }))
    };
  });

const DocHubFields = () => {
  const [loading, setLoading] = useState(true);
  const [updatingFields, setUpdatingFields] = useState(false);
  const [formFields, setFormFields] = useState([]);
  const [editingFormFields, setEditingFormFields] = useState([]);

  const fetchFields = async () => {
    setLoading(true);

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.FORM_FIELDS_LIST,
      method: 'POST'
    });

    setLoading(false);

    if (error) {
      toast.error(error);
      return;
    }

    setFormFields(setUUIDs(response));
  };

  useEffect(() => {
    fetchFields();
  }, []);

  useEffect(() => {
    setEditingFormFields(formFields);
  }, [formFields]);

  const onFieldsSubmit = async () => {
    const errorMessage = validateFields(editingFormFields);
    if (errorMessage) return toast.error(errorMessage);

    toast.info('Please wait, updating fields...');
    const changes = findChanges(formFields, editingFormFields);

    setUpdatingFields(true);

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.FORM_FIELDS_UPDATE_MANY,
      requestBody: changes,
      method: 'PUT'
    });

    setUpdatingFields(false);
    if (error) {
      toast.error(error);
      return;
    }

    toast.success('Fields updated successfully');
    setFormFields(setUUIDs(response));
  };

  useEffect(() => {
    if (!formFields) return;
    const oldAppChoicesString = localStorage.getItem('app-choices');
    let appChoices = [];
    try {
      appChoices = JSON.parse(oldAppChoicesString);
    } catch (e) {}

    const oldAppChoiceIndex = appChoices.findIndex(ac => ac.key === 'formFields');
    if (oldAppChoiceIndex === -1) {
      appChoices.push({ key: 'formFields', values: formFields });
    } else {
      appChoices[oldAppChoiceIndex] = { key: 'formFields', values: formFields };
    }

    localStorage.setItem('app-choices', JSON.stringify(appChoices));
  }, [formFields]);

  return (
    <Container fluid className="h-100 py-3 px-md-3">
      <Card>
        <Card.Body>
          <h6>Dochub Additional Fields</h6>
          <hr className="my-3" />
          {loading ? (
            <Loader />
          ) : (
            <FormBuilder
              disabled={loading || updatingFields}
              fields={editingFormFields}
              onFieldsChange={setEditingFormFields}
              onSubmit={onFieldsSubmit}
            />
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default DocHubFields;
